import React from 'react';
import { connect } from 'react-redux';
import { fetchEventDetails } from '../../actions/event';
import { fetchResponses } from '../../actions/responses';
import Logger from '../../util/Logger';
import ErrorAndRefresh from '../shared/ErrorAndRefresh';
import LoadingAndPulse from '../shared/LoadingAndPulse';
import { NETWORK_CODE_NOT_FOUND } from '../../constants';
import { handleErrorWithDialog } from '../../util/Errors';
import Event from '../../models/Event';
import './AverageBreakdown.css';
import { EventQuestionType } from '../../models/EventQuestionType';
import _ from 'lodash';
import Ideas from '../../models/Ideas';
import EventStats from '../../models/EventStats';
import { stackedBar } from '../charts/templates/stackedBar';
import { ChartGenerator } from '../charts/ChartGenerator';
import {options} from '../charts/options';
import Media from '../../models/Media';
import Response from '../../models/Response';
import { ChartContainer } from '../charts/ChartContainer';
import { BaseDialog } from '../layout/Dialog';
import MultipleRatingReportSummary from './MultipleRatingReportSummary';
import StackRankReportSummary from './StackRankReportSummary';
import { storeScaleData } from '../../features/ai-insights/components/prompts/scalePrompt';

class AverageBreakdown extends React.Component {

  constructor (props) {
    super(props);
    this.state = { viewingImage: null };

    const { event, user } = props;
    if (!user) {
      Logger.assert('Missing user in QuestionStats component');
    }

    if (!event) {
      Logger.assert('Missing event in QuestionStats component');
    }
  }

  componentDidUpdate (prevProps) {
    const { error } = this.props;
    handleErrorWithDialog(error, prevProps.error, 'Unable to load Question Stats');
  }

  loadStats = () => {
    const { fetchEventDetails, fetchResponses, event } = this.props;
    fetchEventDetails(Event.getId(event));
    fetchResponses(Event.getId(event));
  };

  getBreakdown = (event, stats, ideas) => {
    const { id } = this.props;
    let labels = [];
    const type = Event.getQuestionType(event);
    
    // Store scale data for AI Insights when we have scale type
    if (EventQuestionType.isScaleType(type) && Array.isArray(ideas) && ideas.length > 0) {
      const eventId = Event.getId(event);
      // console.log('📊 AverageBreakdown - Raw scale ideas data:', { 
      //   count: ideas.length, 
      //   ideas: ideas,
      //   type,
      //   eventId
      // });
      
      // Deep inspection of ideas data for debugging
      // console.log('📊 AverageBreakdown - Sample idea properties:', {
      //   firstIdea: ideas[0] ? {
      //     name: Ideas.getName(ideas[0]),
      //     description: Ideas.getDescription(ideas[0]),
      //     choiceCount: Ideas.getChoiceCount(ideas[0]),
      //     choicePercent: Ideas.getChoicePercent(ideas[0])
      //   } : 'No ideas available'
      // });
      
      // Store the scale data for prompt generation
      // console.log('📝 AverageBreakdown - Storing ideas data for AI Insights usage');
      storeScaleData(eventId, ideas, {
        eventType: type,
        scaleRange: type === 'scale_5' ? 5 : type === 'scale_10' ? 10 : 5,
        averageScore: EventStats.getSimpleScoreDisplay(stats),
        topScore: EventStats.getMaxScoreDisplay(stats)
      });
    }

    if (EventQuestionType.isScaleType(type)) {
      labels = _.map(ideas, function(idea){
        if(Ideas.getName(idea) === '1' || Ideas.getName(idea) === '5') {
          return `<div class="font-size-sm">${Ideas.getName(idea)} - ${Ideas.getDescription(idea)}</div>`;
        }
        return `<div class="font-size-sm">${Ideas.getName(idea)}</div>`;
      });
    }
    if (EventQuestionType.isBinaryType(type)) {
      labels = _.map(ideas, function(idea){
        return `<div class="font-size-sm">${Ideas.getName(idea)}</div>`;
      });
    }
    if (EventQuestionType.isMultipleType(type)) {
      labels = _.map(ideas, function(idea) {
        let photo;
        const photoAttachment = Response.getPhotoAttachment(idea);
        if (photoAttachment) {
          photo = Media.getPhotoUrl(photoAttachment);
        } else {
          photo = null;
        }
        return (`
          <div class="font-size-sm col no-gutters">
            <div class="col no-gutters">${Ideas.getName(idea)}</div>
            ${ !!photo ? `
              <img
                className="mr-3"
                width="96"
                height="96"
                src="${photo}"
                alt="${Ideas.getName(idea)}" />
            ` : '' }
          </div>
        `.trim());
      });
    }

    const choices = _.map(ideas, function(idea, i){
      // console.log(`📊 Scale choice ${i+1} details:`, {
      //   name: Ideas.getName(idea) || 'N/A',
      //   description: Ideas.getDescription(idea) || 'N/A',
      //   choiceCount: Ideas.getChoiceCount(idea) || 0,
      //   choicePercent: Ideas.getChoicePercent(idea) || 0
      // });
      
      return (`
        <div class="col no-gutters m-auto">
          ${labels[i]}
          <span>
            ${Ideas.getChoicePercent(idea)} (${Ideas.getChoiceCount(idea)} ${Ideas.getChoiceCount(idea) === 1 ? 'vote' : 'votes'})
          </span>
        </div>
      `.trim());
    });

    const percentages = _.map(ideas, (idea) => {
      const choice = Ideas.getName(idea);
      const color = options.colors[0];
      return {
        y: parseInt(Ideas.getChoicePercent(idea).replace('%', ''), 10),
        color: color,
        name: 'score',
        choice: choice,
        voteCount: Ideas.getChoiceCount(idea),
        averageScore: EventStats.getSimpleScoreDisplay(stats),
        topScore: EventStats.getMaxScoreDisplay(stats)
      };
    });

    const emptySpace = _.map(percentages, (percent) => {
      const diff = 100 - (percent.y || 0);
      return {
        y: diff,
        color: options.colors[4],
        name: 'empty-space'
      };
    });
    const data = [
      {
        name: 'empty-space',
        data: emptySpace
      },
      {
        name: 'Votes',
        data: percentages
      }
    ];

    // If there are no choices so far, this can be null, so we make it into an object at least
    // to avoid a crash.
    const topPick = _.maxBy(percentages, point => point.y) || {};

    // Check to see if there are multiple top picks
    const topPicks = _.filter(percentages, (point) =>{
      return topPick.y === point.y;
    });

    const contributedCount = EventStats.getUsersWhoContributedCount(stats);
    return (
      <div className="breakdown-section">
        { type === 'scale' ? (
          <ScaleHeader topPick={topPick} contributedCount={contributedCount} />
        ) : <TopChoiceHeader topPicks={topPicks} contributedCount={contributedCount} />
        }
        <div className="breakdown-h-divider" />
        <div className="row no-gutters average-breakdown-bottom-section">
          <div className="col-12 mt-3">
          </div>
          <div className="col-12">
            <ChartGenerator
              chartId={ !!id ? id : `Breakdown chart Scale` }
              data={ data }
              chartConfig={ stackedBar }
              customChartContainerClass="breakdown-chart-container"
              customChartClass="breakdown-chart"
              forceReflowBecauseOfBug={ true }
              disableAnimation={ this.props.isPdfRender }
              choices={choices}
            />
          </div>
        </div>
      </div>
    );
  };

  render () {
    const { viewingImage } = this.state;
    const {
      padding,
      loading,
      error,
      event,
      stats,
      ideas,
      isPdfRender,
      pdfRatingDistributionByIdeaId,
      pdfCommentsByIdeaId
    } = this.props;
    const averageBreakdownCardClass = `average-breakdown-invite-anybody`;
    if (error) {
      return (
        <div className={ `${averageBreakdownCardClass}` }>
          <ErrorAndRefresh
            error="Unable to load Event"
            // If its a 404, hide the refresh button cause it doesn't exist anyway.
            onRefresh={ error.code === NETWORK_CODE_NOT_FOUND ? null : this.loadStats } />
        </div>
      );
    } else if (loading || !stats || !ideas) {
      // Else if we don't have stats or we are still loading
      return (
        <div className={ `${averageBreakdownCardClass}` }>
          <LoadingAndPulse
            size={ 20 }
            color="#3D9AE2"
            whatIsLoading="Average & Breakdown" />
        </div>
      );
    } else {
      return (
        <div className={ `${averageBreakdownCardClass} print-card-header page-break-avoid` }>
          { EventQuestionType.isMultipleType(Event.getQuestionType(event))
          ? (
            <ChartContainer
              event={ event }
              ideas={ ideas }
              stats={ stats }
              padding={ padding }
              disableAnimation={ isPdfRender }
              onImageClick={ this.handleImageClick }
            />
          ) : EventQuestionType.isMultipleRatingType(Event.getQuestionType(event)) ?
            (
              <MultipleRatingReportSummary
                event={ event }
                ideas={ ideas }
                ratingDistributionByIdeaId={ pdfRatingDistributionByIdeaId }
                commentsByIdeaId={ pdfCommentsByIdeaId } />
            ) : (
              EventQuestionType.isStackRankType(Event.getQuestionType(event)) ? 
                <StackRankReportSummary
                  event={event}
                  ideas={ideas}
                  isPdfRender={ isPdfRender }
                  onImageClick={ this.handleImageClick }
                  />
              : this.getBreakdown(event, stats, ideas)
            ) 
          }

          { !!viewingImage && (
            <BaseDialog
              show={ true }
              closeOnClickOutside={ true }
              body={ (
                <div className="row no-gutters m-n2 mb-n4">

                  <div className="row no-gutters flex-row w-100 mb-2">
                    <div className="flex-fill" />
                    <i className="material-icons clickable" onClick={ this.onDialogHidden }>close</i>
                  </div>

                  <img alt="Choice Media" src={ viewingImage } className="m-auto" style={{
                    maxWidth: '100%',
                    maxHeight: '100%'
                  } }/>
                </div>
              ) }
              hideDialog={ this.onDialogHidden }
            />
          ) }

        </div>
      );
    }
  }

  handleImageClick = (photo) => {
    this.setState({ viewingImage: photo });
  };

  onDialogHidden = () => {
    this.setState({ viewingImage: null });
  }
}

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    loading: state.event.statsLoading,
    error: state.event.statsError
  };
};

const TopChoiceHeader = (props) => (
  <div className="row no-gutters average-breakdown-top-section pt-2 pb-2">
    <div className="col text-center total-votes-label">
      <div className="row no-gutters justify-content-center text-content">TOP CHOICE</div>
      <div className="text-center font-size-xl">
        { props.topPicks.length === 1
          ? <span>{ props.topPicks[0].choice }</span>
          : props.topPicks.length > 1
            ? <span>{props.topPicks.length}-way tie</span>
            : 'N/A'
        }
      </div>
    </div>
  </div>
);

const ScaleHeader = (props) => (
  <div className="row no-gutters average-breakdown-top-section pt-2 pb-2">
    <div className="col">
      <div className="row no-gutters justify-content-center">
        <span>AVERAGE RATING</span>
      </div>
      <div
        className="row no-gutters justify-content-center align-items-end font-size-xxxl"
        style={ { color: `${options.colors[0]}`, marginBottom: '-0.3rem'} }>
        <span className="average-rating-value">{ props.topPick.averageScore || 'N' }</span>
        &nbsp;/&nbsp;
        <span className="average-rating-value">{props.topPick.topScore || 'A'}</span>
      </div>
    </div>
  </div>
);


export default connect(mapStateToProps, {
  fetchEventDetails,
  fetchResponses
})(AverageBreakdown);
